import { useCallback, useEffect, useRef, useState } from 'react'
import { Loader2, Music, Pause, Play, Shuffle, SkipForward, Volume2, VolumeX, X } from 'lucide-react'
import { getAudioImageUrl, resolveMediaUrl, type AudioTrack } from '../../api'
import { adminMediaPlayerClassName } from '../../components/ui'
import { formatDuration } from '../visuals/formatDuration'
import type { AudioSourceQuality, PlayableTrack } from './useAudioPlayer'

type AdminAudioPlayerProps = {
  track: PlayableTrack | null
  activeAudioSource?: AudioSourceQuality | null
  className?: string | undefined
  preferLossless?: boolean
  queuePosition?: number | undefined
  queueTotal?: number | undefined
  shuffleEnabled?: boolean | undefined
  showLosslessToggle?: boolean
  currentTime: number
  duration: number
  isPlaying: boolean
  loading: boolean
  muted: boolean
  onSeek: (time: number) => void
  onSetLosslessPreference?: (enabled: boolean) => void
  onToggleShuffle?: (() => void) | undefined
  onTogglePlayback: () => void
  onToggleMute: () => void
  onVolumeChange: (volume: number) => void
  onStop: () => void
  onNext?: () => void
  volume: number
}

const pc = adminMediaPlayerClassName

export const AdminAudioPlayer = ({
  track,
  activeAudioSource = null,
  className,
  preferLossless = true,
  queuePosition,
  queueTotal,
  shuffleEnabled = false,
  showLosslessToggle = false,
  currentTime,
  duration,
  isPlaying,
  loading,
  muted,
  onSeek,
  onSetLosslessPreference,
  onToggleShuffle,
  onTogglePlayback,
  onToggleMute,
  onVolumeChange,
  onStop,
  onNext,
  volume
}: AdminAudioPlayerProps) => {
  const [playing, setPlaying] = useState(false)
  const progressRef = useRef<HTMLDivElement>(null)
  const volumeRef = useRef<HTMLDivElement>(null)

  const togglePlay = useCallback((): void => {
    onTogglePlayback()
  }, [onTogglePlayback])

  useEffect(() => {
    setPlaying(isPlaying)
  }, [isPlaying])

  const seekTo = useCallback(
    (event: React.MouseEvent<HTMLDivElement>): void => {
      if (!progressRef.current || duration <= 0) return

      const rect = progressRef.current.getBoundingClientRect()
      const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width))
      onSeek(ratio * duration)
    },
    [duration, onSeek]
  )

  const setVolumeTo = useCallback(
    (event: React.MouseEvent<HTMLDivElement>): void => {
      if (!volumeRef.current) return

      const rect = volumeRef.current.getBoundingClientRect()
      const nextVolume = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width))
      onVolumeChange(nextVolume)
    },
    [onVolumeChange]
  )

  const toggleMute = useCallback((): void => {
    onToggleMute()
  }, [onToggleMute])

  if (!track) return null

  const progressRatio = duration > 0 ? Math.max(0, Math.min(1, currentTime / duration)) : 0
  const trackWithImages = 'image_derivatives' in track && 'image_url' in track ? track as AudioTrack : null
  const artworkUrl = trackWithImages ? getAudioImageUrl(trackWithImages, 'thumb') : null
  const hasLossless = track.lossless_status === 'ready' && !!track.lossless_object_key
  const hasPreviewableLossless = hasLossless && track.lossless_storage_backend !== 's3'
  const hasMp3 = !!track.file_url?.trim()
  const hqActive = activeAudioSource === 'flac'
  const hqDisabled = !hasPreviewableLossless || !hasMp3
  const hqTitle = !hasLossless
    ? 'FLAC unavailable'
    : !hasPreviewableLossless
      ? 'FLAC preview unavailable; playing MP3'
    : !hasMp3
      ? 'MP3 unavailable; playing FLAC'
      : hqActive
        ? 'Playing FLAC'
        : 'Playing MP3'
  const queueLabel = queuePosition && queueTotal ? `${queuePosition} of ${queueTotal}` : null
  const secondaryLabel = [track.artist, queueLabel].filter(Boolean).join(' · ')

  return (
    <div className={pc('admin-audio-player', showLosslessToggle && 'has-lossless-toggle', onToggleShuffle && 'has-shuffle', className)}>
      <div className={pc('admin-audio-player-track')}>
        <div className={pc('admin-audio-player-thumb', artworkUrl && 'has-image')}>
          {artworkUrl ? (
            <img alt="" src={resolveMediaUrl(artworkUrl)} />
          ) : (
            <Music size={18} />
          )}
        </div>
        <div className={pc('admin-audio-player-info')}>
          <span className={pc('admin-audio-player-title')}>{track.title}</span>
          {secondaryLabel && <span className={pc('admin-audio-player-artist')}>{secondaryLabel}</span>}
        </div>
      </div>

      <div className={pc('admin-audio-player-controls')}>
        <button
          className={pc('admin-audio-player-btn', 'admin-audio-player-play-btn')}
          disabled={loading}
          onClick={togglePlay}
          type="button"
          aria-label={loading ? 'Loading track' : playing ? 'Pause' : 'Play'}
        >
          {loading ? <Loader2 className={pc('admin-audio-player-spinner')} size={16} /> : playing ? <Pause size={16} /> : <Play size={16} />}
        </button>
        {onToggleShuffle && (
          <button
            aria-label={shuffleEnabled ? 'Disable shuffle' : 'Enable shuffle'}
            aria-pressed={shuffleEnabled}
            className={pc('admin-audio-player-btn', 'admin-audio-player-shuffle', shuffleEnabled && 'active')}
            onClick={onToggleShuffle}
            title={shuffleEnabled ? 'Disable shuffle' : 'Enable shuffle'}
            type="button"
          >
            <Shuffle size={15} />
          </button>
        )}
        {onNext && (
          <button
            className={pc('admin-audio-player-btn')}
            onClick={onNext}
            type="button"
            aria-label="Next track"
          >
            <SkipForward size={15} />
          </button>
        )}
        <span className={pc('admin-audio-player-time')}>
          {formatDuration(Math.floor(currentTime))}
        </span>
        <div
          className={pc('admin-audio-player-progress')}
          onClick={seekTo}
          ref={progressRef}
          role="slider"
          aria-label="Seek"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progressRatio * 100)}
        >
          <div
            className={pc('admin-audio-player-progress-fill')}
            style={{ width: `${progressRatio * 100}%` }}
          />
        </div>
        <span className={pc('admin-audio-player-time')}>
          {formatDuration(Math.floor(duration))}
        </span>
        {showLosslessToggle && (
          <button
            className={pc('admin-audio-player-btn', 'admin-audio-player-hq', hqActive && 'active')}
            disabled={hqDisabled}
            onClick={() => onSetLosslessPreference?.(!preferLossless)}
            title={hqTitle}
            type="button"
            aria-label={hqTitle}
            aria-pressed={hqActive}
          >
            HQ
          </button>
        )}
      </div>

      <div className={pc('admin-audio-player-volume')}>
        <button
          className={pc('admin-audio-player-btn')}
          onClick={toggleMute}
          type="button"
          aria-label={muted ? 'Unmute' : 'Mute'}
        >
          {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
        </button>
        <div
          className={pc('admin-audio-player-volume-bar')}
          onClick={setVolumeTo}
          ref={volumeRef}
          role="slider"
          aria-label="Volume"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round((muted ? 0 : volume) * 100)}
        >
          <div
            className={pc('admin-audio-player-volume-fill')}
            style={{ width: `${(muted ? 0 : volume) * 100}%` }}
          />
        </div>
        <button
          className={pc('admin-audio-player-btn', 'admin-audio-player-stop')}
          onClick={onStop}
          type="button"
          aria-label="Stop and close player"
        >
          <X size={15} />
        </button>
      </div>
    </div>
  )
}
